import {
  useActiveSessions,
  useDeleteSession,
} from "@/hooks/use-shopping-session";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, ShoppingCart, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_app/shopping/")({
  component: ShoppingIndexPage,
});

function ShoppingIndexPage() {
  const navigate = useNavigate();
  const { data: sessions, isLoading } = useActiveSessions();
  const deleteSession = useDeleteSession();

  const canCreate = (sessions?.length ?? 0) < 5;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => navigate({ to: "/labels" })}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex flex-1 items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          <h1 className="text-xl font-bold text-foreground">買い物リスト</h1>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => navigate({ to: "/shopping/new" })}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label="新しい買い物リスト"
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {(!sessions || sessions.length === 0) && (
          <div className="flex flex-col items-center gap-4 py-12">
            <ShoppingCart className="h-12 w-12 text-muted-foreground/30" />
            <p className="text-muted-foreground">買い物リストがありません</p>
            <button
              type="button"
              onClick={() => navigate({ to: "/shopping/new" })}
              className="rounded-full bg-primary px-6 py-3 text-base font-medium text-primary-foreground"
            >
              買い物に行く
            </button>
          </div>
        )}

        {sessions && sessions.length > 0 && (
          <ul className="space-y-3">
            {sessions.map((session) => (
              <li key={session.id}>
                <div className="flex items-center gap-2 rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        to: "/shopping/$sessionId",
                        params: { sessionId: session.id },
                      })
                    }
                    className="flex flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-muted"
                  >
                    <ShoppingCart className="h-5 w-5 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">
                        {session.name ?? "名称未設定"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(session.createdAt).toLocaleDateString(
                          "ja-JP",
                          {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSession.mutate(session.id)}
                    className="shrink-0 p-3 text-muted-foreground hover:text-destructive"
                    aria-label="削除"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {sessions && sessions.length >= 5 && (
          <p className="mt-4 text-center text-sm text-muted-foreground">
            アクティブなリストは最大5件です。完了または削除してから作成してください。
          </p>
        )}
      </div>
    </div>
  );
}
